import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { fetchUserAttributes } from "aws-amplify/auth";
import { listStudentBookings } from "../../lib/leaderboardApi";
import "../Instructor/InstructorSlotsCalendar.css";

function prettyDateTime(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return "--";
  }
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function statusClass(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "booked") return "slot-status slot-status--booked";
  if (normalized === "confirmed") return "slot-status slot-status--confirmed";
  if (normalized === "cancelled" || normalized === "canceled") return "slot-status slot-status--cancelled";
  return "slot-status";
}

function StudentBookingsListPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [studentId, setStudentId] = useState("");
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const attrs = await fetchUserAttributes();
        if (!active) return;

        const sub = String(attrs?.sub || "").trim();
        if (!sub) {
          throw new Error("Missing user ID (sub). Please sign in again.");
        }

        setStudentId(sub);
        const items = await listStudentBookings({ studentId: sub, sortDir: "asc" });
        if (!active) return;

        setBookings(items || []);
      } catch (loadError) {
        if (!active) return;

        const message = loadError?.message || "Unable to load your bookings";
        setError(message);
        setBookings([]);

        if (/not authenticated|sign in|no current user/i.test(message)) {
          navigate("/auth/sign-in", { replace: true });
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [navigate]);

  const orderedBookings = useMemo(() => {
    return [...bookings].sort((a, b) => new Date(a.slotStartAt || 0).getTime() - new Date(b.slotStartAt || 0).getTime());
  }, [bookings]);

  return (
    <section className="instructor-slot-page">
      <Helmet>
        <title>My Class Bookings | ArmorySync</title>
      </Helmet>

      <div className="instructor-slot-hero">
        <div>
          <h1>My Class Bookings</h1>
          <p>List view of your booked and confirmed private classes.</p>
        </div>
        <div className="instructor-slot-actions">
          <Link className="detail-link" to="/profile">Back to Profile</Link>
          <Link className="detail-link" to="/matches">Public Matches</Link>
        </div>
      </div>

      {loading && <p className="instructor-slot-state">Loading bookings...</p>}
      {!loading && error && <p className="instructor-slot-state instructor-slot-state--error">{error}</p>}
      {!loading && !error && (
        <div className="instructor-slot-card" style={{ maxWidth: 1100 }}>
          {orderedBookings.length === 0 && <p className="instructor-slot-state">No bookings found.</p>}
          {orderedBookings.length > 0 && (
            <div className="student-list">
              {orderedBookings.map((booking) => (
                <article key={booking.bookingId || `${booking.slotId}-${booking.studentId}`} className="student-list-item">
                  <header>
                    <strong>{booking.title || "Private Session"}</strong>
                    <span className={statusClass(booking.status)}>{booking.status || "booked"}</span>
                  </header>
                  <p>{prettyDateTime(booking.slotStartAt)} - {prettyDateTime(booking.slotEndAt)}</p>
                  <p>Instructor: {booking.instructorId || "-"}</p>
                  <p>Range: {booking.rangeName || "-"}</p>
                  {booking.notes ? <p>{booking.notes}</p> : null}
                </article>
              ))}
            </div>
          )}

          <p className="slot-mono" style={{ marginTop: 12 }}>Student ID: {studentId || "-"}</p>
        </div>
      )}
    </section>
  );
}

export const StudentBookingsList_Page = {
  path: "/profile/my-bookings",
  element: <StudentBookingsListPage />
};
